import {
  createContext,
  Dispatch,
  FunctionComponent,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";

import { IReducerAction } from "@tether-web-portal/types/common";
import { useMetricsInterval } from "@tether-web-portal/hooks";
import { MetricDataInterval } from "@tether-web-portal/types/metricsTypes";
import { useGetProperty, useGetPropertyDetailWithReadings } from "@tether-web-portal/api/properties/hooks";
import { SeriesType, SeriesTypeReading } from "@tether-web-portal/components/property-graph/GraphUtils";
import { useGetMonitoringHours } from "@tether-web-portal/api/monitoring-hours/hooks";
import {
  useGetMultipleRoomsAggregatedData,
  useGetMultipleRoomLocationsAggregatedData,
} from "@tether-web-portal/api/roomLocations/hooks";
import { RoomType } from "@tether-web-portal/components/create-room/room-icon/RoomIcon";
import { Property } from "@tether-web-portal/types/Property";

import {
  IOccupancyUtilisationTableFilters,
  IOccupancyUtilisationRow,
  IOccupancyBreakdownDataPoint,
  IOccupancyBreakdownFilters,
  IOccupancyBreakdownSummary,
  OccupancyTilesData,
  IOccupancyFloor,
  IOccupancySpace,
  OccupancyExportData,
  IOccupancyPageFilters,
  RoomLocationAggregatedData,
} from "@tether-web-portal/web-workers/occupancy";
import {
  checkIfRoomHasOccupancyDevices,
  checkIfRoomLocationHasActiveOccupancyChildrenDevice,
} from "@tether-web-portal/occupancy/helpers";
import { buildOccupancyModel } from "@tether-web-portal/occupancy/model";
import { logOccupancyDiagnostics } from "@tether-web-portal/occupancy/debug";
import { MonitoringHours } from "@tether-web-portal/types/MonitoringHours";

// Stable empty array to prevent new references on every render
const EMPTY_ROOM_LOCATION_AGGREGATIONS: RoomLocationAggregatedData[] = [];

type IOccupancyContextActionPayloads = {
  SET_IS_LIVE_VIEW: boolean;
  SET_DATA_LAST_UPDATED_AT: Date | null;
  SET_DATA_LOADING: boolean;
  SET_UTILISATION_TABLE_FILTERS: IOccupancyUtilisationTableFilters;
  SET_UTILISATION_TABLE_DATA: IOccupancyUtilisationRow[];
  SET_UTILISATION_LOADING: boolean;
  SET_AVAILABLE_ROOM_TYPES: RoomType[];
  SET_TILES_LOADING: boolean;
  SET_TILES_DATA: OccupancyTilesData;
  SET_MONITORING_HOURS: MonitoringHours[] | null;
  SET_PROPERTY: Property | null;
  SET_AVAILABLE_SPACES: IOccupancySpace[];
  SET_AVAILABLE_FLOORS: IOccupancyFloor[];
  SET_BREAKDOWN_FILTERS: IOccupancyBreakdownFilters;
  SET_BREAKDOWN_DATA: IOccupancyBreakdownDataPoint[];
  SET_LOW_RES_BREAKDOWN_DATA: IOccupancyBreakdownDataPoint[];
  SET_BREAKDOWN_SUMMARY: IOccupancyBreakdownSummary | null;
  SET_BREAKDOWN_LOADING: boolean;
  SET_EXPORT_DATA: OccupancyExportData | null;
  SET_PAGE_FILTERS: IOccupancyPageFilters | undefined;
  SET_UTILISATION_RESULT: {
    tableData: IOccupancyUtilisationRow[];
    availableRoomTypes: RoomType[];
    availableSpaces: IOccupancySpace[];
    availableFloors: IOccupancyFloor[];
  };
  SET_BREAKDOWN_RESULT: {
    breakdownData: IOccupancyBreakdownDataPoint[];
    breakdownSummary: IOccupancyBreakdownSummary | null;
  };
};
type TOccupancyContextActionTypes = keyof IOccupancyContextActionPayloads;

export const OccupancyContextActions: {
  [K in TOccupancyContextActionTypes]: (
    payload: IOccupancyContextActionPayloads[K]
  ) => IReducerAction<TOccupancyContextActionTypes>;
} = {
  SET_IS_LIVE_VIEW: (payload: boolean) => ({
    type: "SET_IS_LIVE_VIEW",
    payload,
  }),
  SET_DATA_LAST_UPDATED_AT: (payload: Date | null) => ({
    type: "SET_DATA_LAST_UPDATED_AT",
    payload,
  }),
  SET_AVAILABLE_ROOM_TYPES: (payload: RoomType[]) => ({
    type: "SET_AVAILABLE_ROOM_TYPES",
    payload,
  }),
  SET_UTILISATION_TABLE_FILTERS: (payload: IOccupancyUtilisationTableFilters) => ({
    type: "SET_UTILISATION_TABLE_FILTERS",
    payload,
  }),
  SET_UTILISATION_TABLE_DATA: (payload: IOccupancyUtilisationRow[]) => ({
    type: "SET_UTILISATION_TABLE_DATA",
    payload,
  }),
  SET_UTILISATION_LOADING: (payload: boolean) => ({
    type: "SET_UTILISATION_LOADING",
    payload,
  }),
  SET_DATA_LOADING: (payload: boolean) => ({
    type: "SET_DATA_LOADING",
    payload,
  }),
  SET_TILES_LOADING: (payload: boolean) => ({
    type: "SET_TILES_LOADING",
    payload,
  }),
  SET_TILES_DATA: (payload: OccupancyTilesData) => ({
    type: "SET_TILES_DATA",
    payload,
  }),
  SET_AVAILABLE_SPACES: (payload: IOccupancySpace[]) => ({
    type: "SET_AVAILABLE_SPACES",
    payload,
  }),
  SET_AVAILABLE_FLOORS: (payload: IOccupancyFloor[]) => ({
    type: "SET_AVAILABLE_FLOORS",
    payload,
  }),
  SET_BREAKDOWN_DATA: (payload: IOccupancyBreakdownDataPoint[]) => ({
    type: "SET_BREAKDOWN_DATA",
    payload,
  }),
  SET_LOW_RES_BREAKDOWN_DATA: (payload: IOccupancyBreakdownDataPoint[]) => ({
    type: "SET_LOW_RES_BREAKDOWN_DATA",
    payload,
  }),
  SET_BREAKDOWN_FILTERS: (payload: IOccupancyBreakdownFilters) => ({
    type: "SET_BREAKDOWN_FILTERS",
    payload,
  }),
  SET_MONITORING_HOURS: (payload: MonitoringHours[] | null) => ({
    type: "SET_MONITORING_HOURS",
    payload,
  }),
  SET_PROPERTY: (payload: Property | null) => ({
    type: "SET_PROPERTY",
    payload,
  }),
  SET_BREAKDOWN_SUMMARY: (payload: IOccupancyBreakdownSummary | null) => ({
    type: "SET_BREAKDOWN_SUMMARY",
    payload,
  }),
  SET_BREAKDOWN_LOADING: (payload: boolean) => ({
    type: "SET_BREAKDOWN_LOADING",
    payload,
  }),
  SET_EXPORT_DATA: (payload: OccupancyExportData | null) => ({
    type: "SET_EXPORT_DATA",
    payload,
  }),
  SET_PAGE_FILTERS: (payload: IOccupancyPageFilters | undefined) => ({
    type: "SET_PAGE_FILTERS",
    payload,
  }),
  SET_UTILISATION_RESULT: (
    payload: IOccupancyContextActionPayloads["SET_UTILISATION_RESULT"]
  ) => ({
    type: "SET_UTILISATION_RESULT",
    payload,
  }),
  SET_BREAKDOWN_RESULT: (
    payload: IOccupancyContextActionPayloads["SET_BREAKDOWN_RESULT"]
  ) => ({
    type: "SET_BREAKDOWN_RESULT",
    payload,
  }),
};

interface IOccupancyContextState {
  dispatcher: Dispatch<IReducerAction<TOccupancyContextActionTypes>>;
  propertyId: string;
  monitoringHours: MonitoringHours[] | null;
  property: Property | null;
  isLiveView: boolean;
  dataLastUpdatedAt: Date | null;
  availableRoomTypes: RoomType[];
  dataLoading: boolean;
  utilisationLoading: boolean;
  utilisationTableFilters: IOccupancyUtilisationTableFilters;
  utilisationTableData: IOccupancyUtilisationRow[];
  tilesData: OccupancyTilesData;
  tilesLoading: boolean;
  availableSpaces: IOccupancySpace[];
  availableFloors: IOccupancyFloor[];
  breakdownLoading: boolean;
  breakdownFilters: IOccupancyBreakdownFilters;
  breakdownData: IOccupancyBreakdownDataPoint[];
  lowResBreakdownData: IOccupancyBreakdownDataPoint[];
  breakdownSummary: IOccupancyBreakdownSummary | null;
  exportData: OccupancyExportData | null;
  pageFilters: IOccupancyPageFilters | undefined;
}

const initialState: IOccupancyContextState = {
  dispatcher: () => null,
  propertyId: "",
  monitoringHours: null,
  property: null,
  isLiveView: false,
  dataLastUpdatedAt: null,
  availableRoomTypes: [],
  utilisationLoading: true,
  utilisationTableFilters: {
    displayBy: "ALL",
    sortBy: "DEFAULT",
  },
  utilisationTableData: [],
  dataLoading: true,
  tilesData: {},
  tilesLoading: true,
  availableSpaces: [],
  availableFloors: [],
  breakdownLoading: true,
  breakdownFilters: {
    floorIndexToDisplay: -1,
    spaceToDisplay: "ALL",
    metricToDisplay: "occupancyCount",
  },
  breakdownData: [],
  lowResBreakdownData: [],
  breakdownSummary: null,
  exportData: null,
  pageFilters: undefined,
};

const OccupancyContext = createContext<IOccupancyContextState>(initialState);

const OccupancyContextReducerHandlers: Record<
  TOccupancyContextActionTypes,
  (
    state: IOccupancyContextState,
    action: IReducerAction<TOccupancyContextActionTypes>
  ) => IOccupancyContextState
> = {
  SET_IS_LIVE_VIEW: (state, action) => ({
    ...state,
    isLiveView: action.payload ?? false,
  }),
  SET_DATA_LAST_UPDATED_AT: (state, action) => ({
    ...state,
    dataLastUpdatedAt: action.payload ?? null,
  }),
  SET_AVAILABLE_ROOM_TYPES: (state, action) => ({
    ...state,
    availableRoomTypes: action.payload ?? [],
  }),
  SET_UTILISATION_TABLE_FILTERS: (state, action) => ({
    ...state,
    utilisationTableFilters: action.payload,
  }),
  SET_UTILISATION_TABLE_DATA: (state, action) => ({
    ...state,
    utilisationTableData: action.payload ?? [],
  }),
  SET_UTILISATION_LOADING: (state, action) => ({
    ...state,
    utilisationLoading: action.payload,
  }),
  SET_DATA_LOADING: (state, action) => ({
    ...state,
    dataLoading: action.payload,
  }),
  SET_TILES_LOADING: (state, action) => ({
    ...state,
    tilesLoading: action.payload,
  }),
  SET_TILES_DATA: (state, action) => ({
    ...state,
    tilesData: action.payload,
  }),
  SET_AVAILABLE_SPACES: (state, action) => ({
    ...state,
    availableSpaces: action.payload,
  }),
  SET_AVAILABLE_FLOORS: (state, action) => ({
    ...state,
    availableFloors: action.payload,
  }),
  SET_BREAKDOWN_DATA: (state, action) => ({
    ...state,
    breakdownData: action.payload,
  }),
  SET_LOW_RES_BREAKDOWN_DATA: (state, action) => ({
    ...state,
    lowResBreakdownData: action.payload,
  }),
  SET_BREAKDOWN_FILTERS: (state, action) => ({
    ...state,
    breakdownFilters: action.payload,
  }),
  SET_MONITORING_HOURS: (state, action) => ({
    ...state,
    monitoringHours: action.payload,
  }),
  SET_PROPERTY: (state, action) => ({
    ...state,
    property: action.payload,
  }),
  SET_BREAKDOWN_SUMMARY: (state, action) => ({
    ...state,
    breakdownSummary: action.payload,
  }),
  SET_BREAKDOWN_LOADING: (state, action) => ({
    ...state,
    breakdownLoading: action.payload,
  }),
  SET_EXPORT_DATA: (state, action) => ({
    ...state,
    exportData: action.payload,
  }),
  SET_PAGE_FILTERS: (state, action) => ({
    ...state,
    pageFilters: action.payload,
  }),
  SET_UTILISATION_RESULT: (state, action) => ({
    ...state,
    utilisationTableData: action.payload.tableData,
    availableRoomTypes: action.payload.availableRoomTypes,
    availableSpaces: action.payload.availableSpaces,
    availableFloors: action.payload.availableFloors,
  }),
  SET_BREAKDOWN_RESULT: (state, action) => ({
    ...state,
    breakdownData: action.payload.breakdownData,
    breakdownSummary: action.payload.breakdownSummary,
  }),
};
const OccupancyContextReducer = (
  state: IOccupancyContextState,
  action: IReducerAction<TOccupancyContextActionTypes>
): IOccupancyContextState => {
  const handler = OccupancyContextReducerHandlers[action.type];
  if (typeof handler === "function") {
    return handler(state, action);
  }
  return state;
};

const ROOM_AGGREGATE_FILTER_METRICS = [
  SeriesTypeReading[SeriesType.OccupancyPeopleCount],
  SeriesTypeReading[SeriesType.Occupied],
  SeriesTypeReading[SeriesType.OccupancyTotalIn],
  SeriesTypeReading[SeriesType.OccupancyTotalOut],
  SeriesTypeReading[SeriesType.FootTraffic],
  SeriesTypeReading[SeriesType.OccupancyRegionPeopleCount],
  SeriesTypeReading[SeriesType.DwellTimeMean],
  SeriesTypeReading[SeriesType.OccupiedPercentage],
  SeriesTypeReading[SeriesType.RegionOccupancy],
];

interface IOccupancyContextProviderProps {
  propertyId: string;
  filters?: IOccupancyPageFilters;
  children: React.ReactNode;
}
export const OccupancyContextProvider: FunctionComponent<IOccupancyContextProviderProps> = (
  props: IOccupancyContextProviderProps
) => {
  const { propertyId, filters, children } = props;

  const [state, dispatch] = useReducer(OccupancyContextReducer, {
    ...initialState,
    propertyId,
  });

  const {
    initialized: metricsIntervalInitialized,
    metricsTimeStamps,
    metricsNumberOfDaysToShow,
    setMetricsInterval,
    metricsCurrentDataInterval,
  } = useMetricsInterval(MetricDataInterval.HOURS_24);

  const monitoringHoursQuery = useGetMonitoringHours({
    scopeId: propertyId,
    includeParent: true,
    scopeType: "property",
  });
  const propertyQuery = useGetProperty(propertyId);
  const propertyInfoQuery = useGetPropertyDetailWithReadings(propertyId);

  const { roomsIdsWithOccupancyDevices, roomLocationIdsWithActiveDevices } = useMemo(() => {
    const roomsIdsWithOccupancyDevices = (propertyInfoQuery?.propertyInfo?.rooms ?? [])
      .filter(checkIfRoomHasOccupancyDevices)
      .map((x) => x.id);

    const roomLocationIdsWithActiveDevices = (propertyInfoQuery?.propertyInfo?.rooms ?? [])
      .filter(checkIfRoomHasOccupancyDevices)
      .flatMap((x) => x.roomLocations)
      .filter(checkIfRoomLocationHasActiveOccupancyChildrenDevice)
      .map((x) => x.id);

    return {
      roomsIdsWithOccupancyDevices,
      roomLocationIdsWithActiveDevices,
    };
  }, [propertyInfoQuery?.propertyInfo?.rooms]);

  const roomAggregateAPIProps = {
    roomIds: roomsIdsWithOccupancyDevices,
    dateRange: metricsTimeStamps,
    filterMetrics: ROOM_AGGREGATE_FILTER_METRICS,
    keepPreviousData: state.isLiveView,
  };

  const highResolutionRoomsAggregatesQuery = useGetMultipleRoomsAggregatedData({
    ...roomAggregateAPIProps,
    resolution: metricsNumberOfDaysToShow <= 1 ? "fifteenminute" : "hour",
  });

  const roomLocationsRegionAggregatesQuery = useGetMultipleRoomLocationsAggregatedData({
    roomLocationIds: roomLocationIdsWithActiveDevices,
    dateRange: metricsTimeStamps,
    resolution: metricsNumberOfDaysToShow <= 1 ? "fifteenminute" : "hour",
    filterMetrics: ROOM_AGGREGATE_FILTER_METRICS,
    keepPreviousData: state.isLiveView,
  });

  // The bar chart rolls up the high-res breakdown client-side (see comment
  // in model.ts:rollUpBreakdownBuckets). 1-day view → hourly bars; longer
  // views → daily bars. No separate low-res query needed.
  const barChartResolution = metricsNumberOfDaysToShow <= 1 ? "hour" : "day";

  // ---------------------------------------------------------------------------
  // Single-source-of-truth model. Every display surface (tiles, utilisation
  // table, heatmap, bar chart, export) is a projection of `occupancyModel`.
  //
  // Built once ALL aggregates have settled — `useAggregatedQueries` streams
  // data incrementally as individual room queries resolve, and a property
  // with many rooms would otherwise rebuild the model 120+ times during
  // load.
  //
  // `metricToDisplay` from breakdownFilters is deliberately NOT part of the
  // memo deps: changing the bar-chart metric dropdown doesn't alter the
  // model, only how the bar chart reads it. Including it caused a multi-
  // second freeze on every metric toggle.
  // ---------------------------------------------------------------------------
  const aggregatesSettled =
    !highResolutionRoomsAggregatesQuery.isLoading &&
    !roomLocationsRegionAggregatesQuery.isLoading;

  const floorIndexToDisplay = state.breakdownFilters.floorIndexToDisplay;
  const spaceToDisplay = state.breakdownFilters.spaceToDisplay;

  // The model build is expensive on large properties (100+ rooms × 30d
  // hourly buckets can take several hundred ms). Running it inside a
  // `useMemo` during render means the main thread is blocked before React
  // can paint the loading skeletons — on a cold refresh that reads to the
  // user as a full freeze because NOTHING appears on screen until the
  // build completes. Running it inside a `useEffect` with state lets
  // React paint the skeletons first, then do the heavy build asynchronously
  // on the next tick. The freeze is still there, but the user sees
  // meaningful loading UI immediately.
  const [occupancyModel, setOccupancyModel] = useState<ReturnType<
    typeof buildOccupancyModel
  > | null>(null);

  useEffect(() => {
    if (
      !propertyInfoQuery?.propertyInfo ||
      !monitoringHoursQuery.monitoringHours ||
      !propertyQuery.property ||
      !aggregatesSettled ||
      !highResolutionRoomsAggregatesQuery.data
    ) {
      setOccupancyModel(null);
      return;
    }
    // Defer one macrotask so the loading UI has a chance to paint before
    // the build claims the main thread. Important: React 18 batches state
    // updates inside microtasks, but paint happens between tasks — so a
    // `setTimeout(0)` (rather than `queueMicrotask`) is what actually
    // yields to the browser painter.
    const handle = setTimeout(() => {
      const next = buildOccupancyModel({
        property: propertyQuery.property!,
        propertyInfo: propertyInfoQuery.propertyInfo!,
        roomAggregations: highResolutionRoomsAggregatesQuery.data!,
        roomLocationAggregations:
          roomLocationsRegionAggregatesQuery.data ?? EMPTY_ROOM_LOCATION_AGGREGATIONS,
        monitoringHours: monitoringHoursQuery.monitoringHours!,
        filters,
        breakdownFilters: { floorIndexToDisplay, spaceToDisplay },
        tableFilters: state.utilisationTableFilters,
        isLiveView: state.isLiveView,
        barChartResolution,
        timeRange: metricsTimeStamps,
      });
      setOccupancyModel(next);
    }, 0);
    return () => clearTimeout(handle);
  }, [
    propertyInfoQuery?.propertyInfo,
    monitoringHoursQuery.monitoringHours,
    propertyQuery.property,
    aggregatesSettled,
    highResolutionRoomsAggregatesQuery.data,
    roomLocationsRegionAggregatesQuery.data,
    filters,
    floorIndexToDisplay,
    spaceToDisplay,
    state.utilisationTableFilters,
    state.isLiveView,
    barChartResolution,
    metricsTimeStamps,
  ]);

  useEffect(() => {
    dispatch(
      OccupancyContextActions.SET_UTILISATION_RESULT({
        tableData: occupancyModel?.utilisation.tableData ?? [],
        availableRoomTypes: occupancyModel?.utilisation.availableRoomTypes ?? [],
        availableSpaces: occupancyModel?.utilisation.availableSpaces ?? [],
        availableFloors: occupancyModel?.utilisation.availableFloors ?? [],
      }),
    );
  }, [occupancyModel?.utilisation]);

  useEffect(() => {
    dispatch(OccupancyContextActions.SET_UTILISATION_LOADING(!occupancyModel || state.dataLoading));
  }, [occupancyModel, state.dataLoading]);

  useEffect(() => {
    dispatch(
      OccupancyContextActions.SET_BREAKDOWN_RESULT({
        breakdownData: occupancyModel?.breakdown.breakdownData ?? [],
        breakdownSummary: occupancyModel?.breakdown.breakdownSummary ?? null,
      }),
    );
  }, [occupancyModel?.breakdown]);

  useEffect(() => {
    dispatch(
      OccupancyContextActions.SET_LOW_RES_BREAKDOWN_DATA(occupancyModel?.lowResBreakdown ?? []),
    );
  }, [occupancyModel?.lowResBreakdown]);

  useEffect(() => {
    dispatch(OccupancyContextActions.SET_BREAKDOWN_LOADING(!occupancyModel || state.dataLoading));
  }, [occupancyModel, state.dataLoading]);

  useEffect(() => {
    dispatch(OccupancyContextActions.SET_TILES_DATA(occupancyModel?.tiles ?? {}));
  }, [occupancyModel?.tiles]);

  useEffect(() => {
    dispatch(
      OccupancyContextActions.SET_TILES_LOADING(
        state.dataLoading || !occupancyModel?.tiles?.ready,
      ),
    );
  }, [state.dataLoading, occupancyModel?.tiles?.ready]);

  useEffect(() => {
    dispatch(OccupancyContextActions.SET_PAGE_FILTERS(filters));
  }, [filters]);

  useEffect(() => {
    dispatch(OccupancyContextActions.SET_EXPORT_DATA(occupancyModel?.export ?? null));
  }, [occupancyModel?.export]);

  // Emit diagnostics to the dev console whenever the model is rebuilt. Logged
  // as a collapsed group so it doesn't spam the console during normal use;
  // expand to inspect per-room peaks and the top building buckets when a
  // number on the page looks wrong.
  useEffect(() => {
    if (occupancyModel?.diagnostics) logOccupancyDiagnostics(occupancyModel.diagnostics);
  }, [occupancyModel?.diagnostics]);

  useEffect(() => {
    dispatch(
      OccupancyContextActions.SET_DATA_LOADING(
        monitoringHoursQuery.isLoading ||
          propertyInfoQuery.isLoading ||
          propertyQuery.isLoading ||
          highResolutionRoomsAggregatesQuery.isLoading
      )
    );
  }, [
    propertyInfoQuery.isLoading,
    propertyQuery.isLoading,
    highResolutionRoomsAggregatesQuery.isLoading,
    monitoringHoursQuery.isLoading,
  ]);

  useEffect(() => {
    const lastUpdated = highResolutionRoomsAggregatesQuery.dataUpdatedAt || 0;
    dispatch(
      OccupancyContextActions.SET_DATA_LAST_UPDATED_AT(lastUpdated > 0 ? new Date(lastUpdated) : null)
    );
  }, [highResolutionRoomsAggregatesQuery.dataUpdatedAt]);

  useEffect(() => {
    dispatch(OccupancyContextActions.SET_MONITORING_HOURS(monitoringHoursQuery.monitoringHours ?? null));
  }, [monitoringHoursQuery.monitoringHours]);

  useEffect(() => {
    dispatch(OccupancyContextActions.SET_PROPERTY(propertyQuery.property ?? null));
  }, [propertyQuery.property]);

  useEffect(() => {
    dispatch(
      OccupancyContextActions.SET_IS_LIVE_VIEW(metricsCurrentDataInterval === MetricDataInterval.MINUTES_15)
    );
  }, [metricsCurrentDataInterval]);

  useEffect(() => {
    if (!state.isLiveView) {
      return;
    }

    // Refresh metric interval times every minute on live view...
    const refreshInterval = setInterval(() => {
      setMetricsInterval(MetricDataInterval.MINUTES_15);
    }, 60000);

    return () => {
      clearInterval(refreshInterval);
    };
  }, [state.isLiveView, setMetricsInterval]);

  const contextValue = useMemo(() => ({ ...state, dispatcher: dispatch }), [state, dispatch]);

  if (!metricsIntervalInitialized) {
    return null;
  }

  return (
    <OccupancyContext.Provider value={contextValue}>
      {children}
    </OccupancyContext.Provider>
  );
};

export const useOccupancyContext = () => useContext(OccupancyContext);
